import {useState} from 'react';
import styles from '../startup.module.scss';

/**
 * A company logo that degrades to a monogram.
 *
 * Every company in the list carries a `logoImgURL`, but a good number of those
 * S3 objects are missing, so the table used to render a column of broken-image
 * glyphs. A bare `<img>` cannot express "this failed" — it has to be caught in
 * JS and swapped, which is what `failed` here is for.
 *
 * The fallback tint is derived from the name rather than random, so the same
 * company keeps the same colour on every page and across reloads.
 */

const TINTS = [
  {bg: '#efe9ff', fg: '#6b4ae0'},
  {bg: '#e6f6f1', fg: '#0f8a6a'},
  {bg: '#fdeef0', fg: '#c94060'},
  {bg: '#fff2e3', fg: '#b46a10'},
  {bg: '#e8f1fe', fg: '#2f6bd4'},
  {bg: '#f2ecfb', fg: '#7a4fb5'},
];

const tintFor = name => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1)
    hash = (hash * 31 + name.charCodeAt(i)) % 997;
  return TINTS[hash % TINTS.length];
};

/** Up to two initials, skipping the suffixes almost every listed name carries. */
const NOISE = ['plc', 'plc.', 'ltd', 'ltd.', 'limited', 'inc', 'inc.'];

const initialsFor = name =>
  (name || '?')
    .split(/[\s-]+/)
    .filter(word => word && NOISE.indexOf(word.toLowerCase()) === -1)
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase() || '?';

export const CompanyLogo = ({src, name = ''}) => {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    const tint = tintFor(name);
    return (
      <span
        className={styles.logoFallback}
        style={{background: tint.bg, color: tint.fg}}
        aria-hidden="true">
        {initialsFor(name)}
      </span>
    );
  }

  return (
    <img
      className={styles.logo}
      src={src}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};

export default CompanyLogo;
